"use client";

import { Button } from "@/components/ui/button";
import ArrowDownRightIcon from "@heroicons/react/24/solid/ArrowDownRightIcon";
import MapPinIcon from "@heroicons/react/24/solid/MapPinIcon";
import BoltIcon from "@heroicons/react/24/solid/BoltIcon";
import TicketIcon from "@heroicons/react/24/outline/TicketIcon";
import PhoneIcon from "@heroicons/react/24/solid/PhoneIcon";
import { parseHtml } from "@/lib/utils";
import { useState } from "react";
import { useVenueDetail } from "@/hooks/venueContext";
import WhatsappButton from "@/components/WhatsappButton";
import {
  WHATSAPP_SECTIONS,
  WHATSAPP_TAGS,
} from "@/@core/enums/whatsapp_tags.enum";
import WhatsappIcon from "@/components/hero-icons/whatsapp-icon";
import dynamic from "next/dynamic";

const VenueDetailShareModal = dynamic(
  () => import("@/components/views/venue-details/new-details/share/Modal"),
);

interface Props extends Campaign.Detail {
  onClick?: () => void;
  titleHeadingLevel: "h2" | "h3";
}

export default function CampingCard(props: Props) {
  const { venue, services } = props;
  const [description] = props.descriptions;
  const { title, description: campaignDescription } = description;
  const firstPhone = venue?.phones?.[0]?.phone;
  const [phoneIsModalOpen, setPhoneModalIsOpen] = useState(false);
  const { setVenue } = useVenueDetail();

  const titleClassName =
    "w-full lg:w-72 text-ellipsis line-clamp-[4] text-slate-800 text-base font-bold";

  const getHeading = () => {
    if (props.titleHeadingLevel == "h2") {
      return <h2 className={titleClassName}>{title}</h2>;
    }

    return <h3 className={titleClassName}>{title}</h3>;
  };

  return (
    <>
      <article className="min-h-[261px] max-h-[400px] w-full p-2 bg-white rounded-[32px] border border-gray-200 justify-start items-start flex">
        <div className="min-h-[261px] max-h-[400px] border border-dashed !w-full !h-full border-assistant flex rounded-[24px]">
          <button
            type="button"
            onClick={props.onClick}
            className="lg:!h-full bg-stone-50 !w-[44px] rounded-tl-3xl rounded-bl-3xl  flex justify-center items-center border-r-2 border-dashed border-r-assistant"
          >
            <div className="flex gap-2 items-center justify-center rotate-90 font-bold text-sm whitespace-nowrap">
              Kampanyayı Görüntüle <TicketIcon className="size-4" />
            </div>
          </button>

          <section className="flex flex-col gap-2 p-4 w-full">
            <div className="flex flex-1 w-full">{getHeading()}</div>

            {services.length > 0 && (
              <div className="flex gap-1 max-w-[289px] py-1 flex-wrap">
                {services.map(({ service }: any, index: number) => {
                  return (
                    <div
                      key={index}
                      className="flex gap-1 whitespace-nowrap h-[17px] px-1.5 py-0.5 text-[10px] rounded-2xl bg-[#EDE4FF] items-center text-[#6528F7] font-medium"
                    >
                      <BoltIcon className="size-3" />
                      {service?.descriptions?.[0]?.title}
                    </div>
                  );
                })}
              </div>
            )}

            <div>
              <span
                dangerouslySetInnerHTML={{
                  __html: parseHtml(campaignDescription),
                }}
                className="text-ellipsis line-clamp-4 text-gray-400 text-xs md:text-sm font-medium"
              ></span>
            </div>

            <footer className="flex flex-col justify-end items-start h-full gap-2">
              <div className="flex gap-2 flex-col">
                <div className="flex gap-1 items-center">
                  <ArrowDownRightIcon className="size-4 text-gray-400" />
                  <p className="text-gray-400 text-xs font-medium">
                    {venue?.descriptions?.[0]?.title}
                  </p>
                </div>

                <div className="flex gap-1 items-center">
                  <MapPinIcon className="size-4 text-gray-400" />
                  <p className="text-center text-gray-400 text-xs font-medium">
                    {venue?.district?.title}, {venue?.district?.city?.title}
                  </p>
                </div>
              </div>

              <div className="flex justify-end w-full gap-2 flex-wrap">
                <Button
                  disabled={!firstPhone}
                  className="!w-16 !h-6 flex gap-2 text-sm !p-2"
                  variant="assistant"
                  onClick={() => {
                    setVenue(props.venue as Venue.List);
                    setPhoneModalIsOpen(true);
                  }}
                >
                  <PhoneIcon className="size-4" /> Ara
                </Button>

                <WhatsappButton
                  tag={WHATSAPP_TAGS.CAMPAIGN_DETAIL}
                  phoneNumber={venue.whatsappPhoneNumber}
                  venue={venue as Venue.List}
                  section={WHATSAPP_SECTIONS.CAMPAIGN}
                  className="!h-6 flex gap-2 !p-2 text-sm"
                  translateValues={{ campaign: props.descriptions?.[0]?.title }}
                >
                  <WhatsappIcon className="size-4" /> Kampanya Sor
                </WhatsappButton>
              </div>
            </footer>
          </section>
        </div>
      </article>

      {phoneIsModalOpen && (
        <VenueDetailShareModal
          onClose={() => setPhoneModalIsOpen(false)}
          isShareOpen={phoneIsModalOpen}
        />
      )}
    </>
  );
}
